/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal} from '@angular/core';

import {
  findCreativeAdvertiserMismatch,
  findFolioCreativeAdvertisersMismatch,
  findOrderCreativeAdvertisersMismatch,
  isFolioAdvertiserMismatchWarning
} from './creative-advertiser-mismatch';
import {CreativeAdvertiserMismatchService} from './creative-advertiser-mismatch.service';

@Component({
  standalone: false,
  selector: 'adn-creative-advertiser-mismatch-fix',
  template: `
    <p *ngIf="visible()">
      <a (click)="updateAdvertiser()">{{ updating() ? 'Updating advertiser…' : linkText() }}</a>
    </p>
  `
})
export class CreativeAdvertiserMismatchFixComponent implements OnChanges {
  @Input() creative: any;
  @Input() order: any;
  @Input() folio: any;
  @Input() warnings: any;
  @Input() readOnly: boolean;
  @Input() libraryCreative = false;
  @Output() updated = new EventEmitter<any>();

  readonly visible = signal(false);
  readonly updating = signal(false);
  readonly linkText = signal('Update the advertiser to match');

  constructor(private readonly mismatchService: CreativeAdvertiserMismatchService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.warnings || changes.creative || changes.order || changes.folio || changes.readOnly) {
      this.syncVisibility();
    }
  }

  updateAdvertiser(): void {
    if (this.updating() || this.readOnly) {
      return;
    }
    const update = this.folio?.id && !this.creative?.id
      ? this.mismatchService.updateFolioCreativesAdvertiser(this.folio)
      : this.order?.id && !this.creative?.id
        ? this.mismatchService.updateOrderCreativesAdvertiser(this.order)
        : this.creative?.id
          ? this.mismatchService.updateAdvertiser(this.creative, {libraryCreative: this.libraryCreative})
          : null;
    if (!update) {
      return;
    }
    this.updating.set(true);
    update.then((saved) => {
      this.updating.set(false);
      this.syncVisibility();
      this.updated.emit(saved);
    }, () => {
      this.updating.set(false);
    });
  }

  private syncVisibility(): void {
    const warnings = this.warnings
      || this.creative?.validationWarnings
      || this.order?.validationWarnings
      || this.folio?.validationWarnings;
    const orderMismatch = findOrderCreativeAdvertisersMismatch(warnings);
    if (orderMismatch && this.order?.id && !this.readOnly) {
      this.visible.set(true);
      this.linkText.set('Update the advertiser on the affected creatives to match the order');
      return;
    }
    const folioMismatch = findFolioCreativeAdvertisersMismatch(warnings);
    if (folioMismatch && this.folio?.id && !this.readOnly) {
      this.visible.set(true);
      this.linkText.set('Update the advertiser on the affected creatives to match the folio');
      return;
    }
    const mismatch = findCreativeAdvertiserMismatch(warnings);
    this.visible.set(!!mismatch && !!this.creative?.id && !this.readOnly);
    this.linkText.set(isFolioAdvertiserMismatchWarning(mismatch)
      ? 'Update the advertiser to match the folio'
      : 'Update the advertiser to match the order');
  }
}
